import { FetchResult, gql, useMutation, useQuery } from '@apollo/client';
import SpecimensIcon from 'assets/icons/commonsIcon/specimens_icon.svg?react';
import MultilingualText from 'components/Multilingual/MultilingualText';
import NextPreviousButton from 'components/common/NextPreviousButton';
import LeavesIcon from 'components/svg/LeavesIcon';
import { UserContext } from 'contexts/user/UserContext';
import { CREATE_USER_SPECIMAN } from 'pages/Bioregion/Specimen/queries';
import { CLAIM_ANONYMOUS_ROUTE_USER_RATING, CREATE_ROUTE_STOP_USER_STATUS } from 'pages/Commons/Quest/queries';
import { QuizProgressLocalStorage, QuizProgressLocalStorageRoute, RouteRatingsLocalStorage, isPermanentlyRejectedRatingClaim, markCompletedRouteRatingClaims, readStoredRouteRatings, writeStoredRouteRatings } from 'pages/Commons/Quest/useLocalStorageRouteData';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Route } from 'services/graphqltypes';
import IntlMessages from 'helpers/IntlMessages';

const readStoredArray = <T,>(key: string): T[] => {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    return Array.isArray(value) ? value as T[] : [];
  } catch { return []; }
};

const removeStoredItem = (key: string) => {
  try { localStorage.removeItem(key); } catch {
    // A blocked store has no saved progress to remove.
  }
};

/** Checks if there are self-guided routes stops statuses saved in local storage; if yes, store to database, and show info modal about it. */
const RoutesProgressSaved = ({ onClose }: { onClose?: (redirectPath?: string) => void }) => {
  const { currentUser } = useContext(UserContext);

  const localStorageQuizProgress = useRef<QuizProgressLocalStorage>(readStoredArray<QuizProgressLocalStorageRoute>('quizProgress'));
  const localStorageRouteRatings = useRef<RouteRatingsLocalStorage>(readStoredRouteRatings());
  const effectHasRun = useRef(false); // flag to prevent useEffect from running again when routesUserStatusData is updated by cache
  const redirectPath = useRef<string>();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const hasProgressToShow = useRef(localStorageQuizProgress.current.length > 0
    || readStoredArray<string>('specimenObserved').length > 0).current;

  /* */
  /* State */
  /* */
  const [showModal, setShowModal] = useState(true);
  const [updatedRoutes, setUpdatedRoutes] = useState<{ // each object in the array represents a single route whose stops were present in local storage and have been saved to the database
    routeId: string,
    specimenCount: number,
    impactPointsCount: number,
    stopsId: string[],
    routeName: JSON,
  }[]>([]);

  /* */
  /* Query */
  /* */
  // create query string based on the routes in localStorage
  // query for each route in localStorage, so that we know what mutations have to run
  const ROUTES_USER_STATUS = () => {
    if (localStorageQuizProgress.current.length === 0) {
      return gql`
      query DummyQuery {
        __typename
      }`;
    }

    return gql`
      query routesUserStatus {
        ${localStorageQuizProgress.current.map((route: QuizProgressLocalStorageRoute, index: number) => {
      return `route_${index}:route(id: "${route.routeId}") {
            id
            name
            slug
            routeStops {
              nodes {
                id
                currentUserHasCompleted
                currentUserTries
                impactPoints
                specimenId
              }
            }
          }`;
    })}
      }
    `;
  };

  const { loading, data: routesUserStatusData } = useQuery(ROUTES_USER_STATUS(), { skip: (localStorageQuizProgress.current.length === 0) });

  /* */
  /* Mutations */
  /* */
  const [createRouteStopUserStatus] = useMutation(CREATE_ROUTE_STOP_USER_STATUS);
  const [claimAnonymousRouteUserRating] = useMutation(CLAIM_ANONYMOUS_ROUTE_USER_RATING);
  // const [updateRouteStopUserStatus] = useMutation(UPDATE_ROUTE_STOP_USER_STATUS); we don't keep track of this for now
  const [createUserSpeciman] = useMutation(CREATE_USER_SPECIMAN);

  // for each route stop status in local storage, 
  //     run a createRouteStopUserStatus mutation if it is not yet saved in the db
  //     run an updateRouteStopUserStatus mutation if it has been saved in the db
  // for each specimen in localStorageSpecimenObserved,
  //     run a createUserSpeciman mutation if userSpeciman will not be created by the RouteStopUserStatus Mutation above.
  useEffect(() => {
    if (!loading && currentUser?.id && !effectHasRun.current) {
      effectHasRun.current = true
      const localStorageSpecimenObserved = readStoredArray<string>('specimenObserved');

      // create array of mutations promises
      const createRouteStopUserStatusMutations: Promise<FetchResult<any>>[] = [];
      const updateRouteStopUserStatusMutations: Promise<FetchResult<any>>[] = [];
      const createUserSpecimanMutation: Promise<FetchResult<any>>[] = [];
      const claimAnonymousRouteUserRatingMutations = localStorageRouteRatings.current
        // Legacy ratings have no token to claim. Keep their browser marker so
        // the same guest cannot submit a second rating for the Quest.
        .filter((rating) => rating.claimToken)
        .map((rating) => ({
          rating,
          promise: claimAnonymousRouteUserRating({
          variables: {
            ratingId: rating.routeUserRatingId,
            claimToken: rating.claimToken,
          },
          }),
        }));

      routesUserStatusData && localStorageQuizProgress.current.forEach(
        (localStorageRoute: QuizProgressLocalStorageRoute) => {
          // find the corresponding route in the query result
          const routeFromQuery = Object.values(routesUserStatusData).find((route: any) =>
            route.id === localStorageRoute.routeId) as Route;
          if (!redirectPath.current && routeFromQuery.slug) {
            redirectPath.current = `/quest/${routeFromQuery.slug}/map`;
          }

          localStorageRoute.stopsStatus.forEach((stopStatus) => {
            const stopFromQuery = routeFromQuery.routeStops.nodes.find((stop) => stop.id === stopStatus.stopId);
            if (stopStatus.status) {
              // remove the specimen from localStorageSpecimenObserved array, so that we don't double create userSpecimen
              localStorageSpecimenObserved.splice(localStorageSpecimenObserved.indexOf(stopFromQuery.specimenId), 1);
              // if user has not completed this stop and saved in the database in the past, add into create mutation array
              if (!stopFromQuery.currentUserHasCompleted) {
                // preparing date to show in Modal
                setUpdatedRoutes((prevState) => {
                  const updatedState = [...prevState];
                  const routeToUpdateIndex = updatedState.findIndex((route) => route.routeId === localStorageRoute.routeId);
                  if (routeToUpdateIndex !== -1) {
                    if (!updatedState[routeToUpdateIndex].stopsId.includes(stopStatus.stopId)) {
                      updatedState[routeToUpdateIndex].stopsId.push(stopStatus.stopId);
                      updatedState[routeToUpdateIndex].specimenCount += 1;
                      updatedState[routeToUpdateIndex].impactPointsCount += stopFromQuery.impactPoints;
                    }
                  } else {
                    updatedState.push({
                      routeId: localStorageRoute.routeId,
                      routeName: routeFromQuery.name as JSON,
                      specimenCount: 1,
                      impactPointsCount: stopFromQuery.impactPoints,
                      stopsId: [stopStatus.stopId],
                    });
                  }
                  return updatedState;
                });
                // update mutation array
                createRouteStopUserStatusMutations.push(createRouteStopUserStatus({
                  variables: {
                    routeStopId: stopStatus.stopId,
                  }
                }));
              } else {
                // if user has completed this stop and saved in the database in the past, add into update mutation array
                // we don't keep of track of this for now
                // updateRouteStopUserStatusMutations.push(updateRouteStopUserStatus(
                //   {
                //     variables: {
                //       routeStopId: stopStatus.stopId,
                //       createdByUserId: currentUser.id,
                //       tries: stopFromQuery.currentUserTries + 1
                //     }
                //   }
                // ));
              }
            }
          }
          );
        });

      // update mutation array  
      localStorageSpecimenObserved.forEach((specimenId) => {
        createUserSpecimanMutation.push(
          createUserSpeciman({
            variables: {
              specimenId,
              userId: currentUser.id,
            },
          })
        );
      });

      const otherMutations = [...createRouteStopUserStatusMutations, ...createUserSpecimanMutation, ...updateRouteStopUserStatusMutations];
      if (otherMutations.length === 0 && claimAnonymousRouteUserRatingMutations.length === 0) {
        removeStoredItem('quizProgress');
        removeStoredItem('specimenObserved');
        if (!hasProgressToShow) onCloseRef.current?.();
        return;
      }

      Promise.all([
        Promise.allSettled(otherMutations),
        Promise.allSettled(claimAnonymousRouteUserRatingMutations.map(({ promise }) => promise)),
      ]).then(([otherResults, claimResults]) => {
        const failedOtherMutation = otherResults.some((result) => result.status === 'rejected');
        const successfulClaims = claimResults
          .map((result, index) => result.status === 'fulfilled' ? claimAnonymousRouteUserRatingMutations[index].rating.routeUserRatingId : null)
          .filter((ratingId): ratingId is string => !!ratingId);
        const permanentlyRejectedClaims = claimResults
          .map((result, index) => result.status === 'rejected'
            && isPermanentlyRejectedRatingClaim(result.reason)
            ? claimAnonymousRouteUserRatingMutations[index].rating.routeUserRatingId : null)
          .filter((ratingId): ratingId is string => !!ratingId);
        const clearedClaims = [...successfulClaims, ...permanentlyRejectedClaims];
        if (clearedClaims.length > 0) {
          markCompletedRouteRatingClaims(clearedClaims);
          const latestRatings = readStoredRouteRatings();
          const remainingRatings = latestRatings.filter((rating) => !clearedClaims.includes(rating.routeUserRatingId));
          localStorageRouteRatings.current = remainingRatings;
          if (remainingRatings.length > 0) {
            writeStoredRouteRatings(remainingRatings);
          } else {
            removeStoredItem('routeRatings');
            try { sessionStorage.removeItem('routeRatings'); } catch {
              // The browser may block session storage too.
            }
          }
        }
        if (!failedOtherMutation) {
          removeStoredItem('quizProgress');
          removeStoredItem('specimenObserved');
        }
      }).catch((error: any) => {
        if (error) {
          console.error('progress save error', error); // we intentionally skip user notification for the error
        }
      }).finally(() => {
        // Feedback attribution needs no progress dialog. Advance the overlay
        // queue after the claim attempt, retaining transient failures for retry.
        if (!hasProgressToShow) onCloseRef.current?.();
      });
    }
  }, [loading, routesUserStatusData, createRouteStopUserStatus, createUserSpeciman, claimAnonymousRouteUserRating, currentUser?.id, hasProgressToShow]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    onClose?.(redirectPath.current);
  }, [setShowModal, onClose]);

  /* */
  /* Render */
  /* */
  if (!hasProgressToShow) return null;

  return (
    <Modal
      show={showModal}
      onHide={handleCloseModal}
      backdrop='static' /* prevent closing modal when clicked outside it*/
      size='lg'
      className='rounded-modal'
    >
      <Modal.Header closeButton className='mr-3 mt-3 p-0' />
      <Modal.Body className='pt-0 mt-0 text-center'>
        {hasProgressToShow
          ?
          loading
            ? <div className="loading-relative-position my-2" />
            : <div className='my-2'>
              <h1 className='text-center'><IntlMessages id="route.progress-saved" /></h1>
              {updatedRoutes.map((route, index) => {
                return (
                  <div className='text-center' key={index}>
                    <span className='font-weight-bold'>
                      <MultilingualText value={route.routeName} />:
                    </span>
                    <span className='ml-1'>
                      +{route.impactPointsCount} <LeavesIcon color='#60b6aa' size={25} className='ml-1 my-auto' /> +{route.specimenCount} <SpecimensIcon width={20} height={30} stroke="#60b6aa" className='ml-1 my-auto' />
                    </span>
                  </div>
                );
              })}
            </div>
          : null
        }

        <NextPreviousButton
          onClick={handleCloseModal}
          buttonTextId='general.close'
        />

      </Modal.Body>
    </Modal>
  );
};

export default RoutesProgressSaved;
